<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_LOAN_REPAYMENT_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="LaonRepaymentExpectationTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key=""
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dueDate)="data">
        <span class="text-nowrap">
          {{ data.value ? formatDate(data.value) : 0 }}
        </span>
      </template>

      <template #cell(principal)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>

      <template #cell(interest)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>

      <template #cell(fee)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>

      <template #cell(total)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>

      <template #cell(repaymentStatus)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="loanRepaymentExpectationRules">
        <b-form>
          <b-row class="match-height">
            <!-- Loan Account Number -->
            <b-col md="6">
              <InputField
                id="loan-account-number"
                v-model="filter.accountNumber"
                :label="$t('Loan Account Number')"
                :placeholder="$t('Enter Loan Account Number')"
                type="text"
                for="loan-account-number"
              />
            </b-col>
            <!-- Loan Account Number -->
            <!-- Account Officer -->
            <b-col md="6">
              <SelectField
                id="accountOfficer"
                v-model="filter.accountOfficerCode"
                :reduce="option => option.value"
                :label="$t('Account Officer')"
                :placeholder="$t('Select')"
                :options="accountOfficerOptions"
                item="label"
              />
            </b-col>
          <!-- Account Officer -->
          </b-row>
          <b-row class="match-height">
            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="start-date"
                v-model="filter.startDate"
                rules="required"
                :min-date="new Date()"
                :label="$t('Start Date')"
                :error-name="$t('Start Date')"
                :config="datePickerConfig"
                :placeholder="$t('Select Start Date')"
              />
            </b-col>
            <!-- Start Date -->
            <!-- End Date -->
            <b-col md="6">
              <DatePickerField
                id="end-date"
                v-model="filter.endDate"
                rules="required"
                :min-date="new Date()"
                :label="$t('End Date')"
                :error-name="$t('End Date')"
                :config="datePickerConfig"
                :placeholder="$t('Select End Date')"
              />
            <!-- Start Date -->
            </b-col></b-row>
          <b-row class="match-height">
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                :label="$t('Branch')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->

            <!-- Product -->
            <b-col md="6">
              <SelectField
                id="product"
                v-model="filter.productCode"
                :reduce="option => option.value"
                :label="$t('Product Name')"
                :placeholder="$t('Select')"
                :options="productOptions"
                item="label"
              />
            </b-col>
            <!-- Product -->
            <!-- IPPIS/Oracle ID -->
            <b-col md="6">
              <InputField
                id="ippis-oracle-id"
                v-model="filter.employeeID"
                :label="$t('IPPIS/Oracle ID')"
                :placeholder="$t('Enter IPPIS/Oracle ID')"
                type="text"
                for="ippis-oracle-id"
              />
            </b-col>
            <!--IPPIS/Oracle ID -->
            <b-col md="6">
              <SelectField
                id="repayment-status"
                v-model="filter.repaymentStatus"
                :reduce="option => option.value"
                :label="$t('Repayment Status')"
                :placeholder="$t('Select')"
                :options="translatedRepaymentStatusOptions"
                item="label"
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6">
              <SwitchButton
                v-model="filter.excludeClosedAccounts"
                :label="$t('Exclude Closed Accounts')"
                name="exclude-closed-account"
              />
            </b-col>
            <b-col md="6">
              <SwitchButton
                v-model="filter.excludeFullyPaidInstallments"
                :label="$t('Exclude Fully Paid Installments')"
                name="exclude-fully-paid-installments"
              />
            </b-col>
          </b-row>

        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BForm, BDropdown, BDropdownItem, BBadge,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import XLSX from 'xlsx'
import { heightFade } from '@core/directives/animations'
import getStatusColor from '@/helpers/getStatusColor'
import { toBalance, formatAmount } from '@/helpers/formatAmount'
import toNumber from '@/helpers/toNumber'
import { toCustomDate, customDate, toDateWithYearFirst } from '@/helpers/formatDate'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import SelectField from '../../../forms/SelectField.vue'
import InputField from '../../../forms/InputField.vue'
import SwitchButton from '../../../components/switch/Switch.vue'
import Button from '../../../components/button/Button.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    InputField,
    SelectField,
    BBadge,
    BModal,
    BForm,
    BDropdown,
    BDropdownItem,
    TableEntries,
    SwitchButton,
    DatePickerField,
    ValidationObserver,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    const todayDay = new Date(today)
    return {
      toDateWithYearFirst,
      firstDate: firstDay,
      todayDate: todayDay,
      today,
      getStatusColor,
      currentDate: '',
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isDisabled: true,
      isBusy: false,
      required,
      modalTitle: this.$t('Loan Repayment Expectation Report'),
      sortBy: 'accountName',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'product', sortable: true, label: 'Product Name' },
        { key: 'phoneNumber', sortable: true, label: 'Customer Phone Number' },
        { key: 'employeeID', sortable: true, label: 'IPPIS/Oracle ID' },
        { key: 'accountOfficer', sortable: true, label: 'Account Officer' },
        { key: 'dueDate', sortable: true, label: 'Due Date' },
        { key: 'principal', sortable: true, label: 'Principal' },
        { key: 'interest', sortable: true, label: 'Interest' },
        { key: 'fee', sortable: true, label: 'Fee' },
        { key: 'total', sortable: true, label: 'Total' },
        { key: 'repaymentStatus', sortable: true, label: 'Repayment Status' },
      ],
      filter: {
        accountNumber: '',
        startDate: null,
        endDate: null,
        accountOfficerCode: '',
        repaymentStatus: '',
        branchCode: '',
        employeeID: '',
        productCode: '',
        excludeClosedAccounts: true,
        excludeFullyPaidInstallments: true,
      },
      productOptions: [],
      repaymentStatusOptions: [
        { label: 'Fully Paid', value: 1 },
        { label: 'Partially Paid', value: 2 },
        { label: 'Due And Unpaid', value: 3 },
        { label: 'Not Yet Due', value: 4 },
        { label: 'Early Termination', value: 5 },
      ],
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        defaultDate: '',
      },
      accountOfficerOptions: [],
      branchOptions: [],
      allSearchedData: [],
      branchRefCode: '',
      reportData: [],
      selected: '',
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map(item => ({
        'Account Name': item.accountName,
        'Account Number': item.accountNumber,
        'Account Officer': item.accountOfficer,
        'Product Name': item.product,
        'Customer Phone Number': item.phoneNumber,
        'IPPIS/Oracle ID': item.employeeID,
        'Due Date': this.formatCustomDate(item.dueDate),
        Principal: this.toAmount(item.principal),
        Interest: this.toAmount(item.interest),
        Fee: this.toAmount(item.fee),
        Total: this.toAmount(item.total),
        'Repayment Status': item.repaymentStatus,
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedRepaymentStatusOptions() {
      return this.repaymentStatusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchLoanRepaymentExpectation()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchLoanRepaymentExpectation()
    },
  },
  mounted() {
    this.fetchAccountOfficer()
    this.fetchProduct()
    this.fetchBranches()
    this.fetchLoanRepaymentExpectation()
  },
  methods: {
    ...mapActions({
      getLoanRepaymentExpectation: 'reports/getLoanRepaymentExpectation',
      getAccountOfficer: 'account/getAccountOfficerList',
      getBranch: 'operations/getBranchList',
      getBranchByCode: 'operations/getBranchByCode',
      getProductByTypes: 'account/getProductByTypes',
    }),
    resetFilters() {
      this.filter = { ...null }
      this.filter.excludeClosedAccounts = true
      this.filter.excludeFullyPaidInstallments = true
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.startDate = null
      this.filter.endDate = null
      this.filter.excludeClosedAccounts = true
      this.filter.excludeFullyPaidInstallments = true
      this.fetchLoanRepaymentExpectation()
    },
    async fetchProduct() {
      try {
        const response = await this.getProductByTypes({
          type1: 'Loan',
          permission: reportPermissions.LOAN_REPAYMENT_REPORT,
        })
        const { data } = response
        if (data.data) {
          this.productOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchBranches() {
      try {
        const response = await this.getBranch({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.LOAN_REPAYMENT_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.branchOptions = data.data
            .filter(dataItem => dataItem.status === 'Active')
            .map(item => ({
              label: item.name,
              value: item.code.toString(),
            }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchAccountOfficer() {
      try {
        const response = await this.getAccountOfficer({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.LOAN_REPAYMENT_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.accountOfficerOptions = data.data.map(item => ({ label: item.name, value: item.staffID }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    handleSubmit(bvModalEvt) {
      try {
        bvModalEvt.preventDefault()
        this.$refs.loanRepaymentExpectationRules.validate().then(async success => {
          if (success) {
            this.fetchLoanRepaymentExpectation()
          }
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.hideModal('modal-filter')
      }
    },
    async fetchLoanRepaymentExpectation() {
      try {
        this.isBusy = true
        if (this.filter.startDate === null && this.filter.endDate === null) {
          this.currentDate = {
            startDate: this.toDateWithYearFirst(this.firstDate),
            endDate: this.toDateWithYearFirst(this.todayDate),
            excludeClosedAccounts: this.filter.excludeClosedAccounts,
            excludeFullyPaidInstallments: this.filter.excludeFullyPaidInstallments,
          }
        }

        this.hideModal('modal-filter')
        const response = await this.getLoanRepaymentExpectation({
          search: this.filter.startDate !== null ? this.filter : this.currentDate,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
          this.totalCount = data.recordCount
          this.isBusy = false
          this.isDisabled = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.getLoanRepaymentExpectation({
          search: this.filter.startDate !== null ? this.filter : this.currentDate,
          page: 1,
          limit: 1000000,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          let csvContent = 'data:text/csv;charset=utf-8,'
          csvContent += [
            [orgName],
            ['Loan Repayment Expectation Report'],
            [`${(this.filter.startDate !== null && this.filter.endDate !== null) ? `Date Range: ${this.formatCustomDate(this.filter.startDate)} - ${this.formatCustomDate(this.filter.endDate)}` : 'Date Range: All'}`],
            [`${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`],
            [],

            Object.keys(arrData[0]).join(','),
            ...arrData.map(item => Object.values(item).join(',')),
          ]
            .join('\n')
            .replace(/(^\[)|(\]$)/gm, '')

          const dataValue = encodeURI(csvContent)
          const link = document.createElement('a')
          link.setAttribute('href', dataValue)
          link.setAttribute('download', 'Loan-Repayment-Expectation-Report.csv')
          link.click()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.getLoanRepaymentExpectation({
          search: this.filter.startDate !== null ? this.filter : this.currentDate,
          page: 1,
          limit: 1000000,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          const filename = 'Loan-Repayment-Expectation-Report.xlsx'
          const headerLists = [
            Object.keys(arrData[0]),
          ]

          const Heading = [
            [orgName],
            ['Loan Repayment Expectation Report'],
            [`${(this.filter.startDate !== null && this.filter.endDate !== null) ? `Date Range: ${this.formatCustomDate(this.filter.startDate)} - ${this.formatCustomDate(this.filter.endDate)}` : 'Date Range: All'}`],
            [`${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`],
            [],
            headerLists[0], // Headers lists
          ]
          const Data = arrData // All data lists
          const ws = XLSX.utils.aoa_to_sheet(Heading)
          XLSX.utils.sheet_add_json(ws, Data, {
            skipHeader: true,
            origin: -1,
          })
          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, 'Loan Repayment Exp. Report')
          XLSX.writeFile(wb, filename)
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },

    formatAmount: toBalance,
    toAmount: formatAmount,
    formatNumber: toNumber,
    formatCustomDate: toCustomDate,
    formatDate: customDate,
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
